import { useState, useEffect, useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { MessageSquare } from "lucide-react";

import { useWebSocket } from "../context/WebSocketContext";
import { useAuth } from "../context/AuthContext";

import ClientSidebar from "../components/conversations/ClientSidebar";
import ConversationsSidebar from "../components/conversations/ConversationsSidebar";
import ChatHeader from "../components/conversations/chat/ChatHeader";
import ChatMessages from "../components/conversations/chat/ChatMessages";
import MessageComposer from "../components/conversations/chat/MessageComposer";
import DirectMessageList from "../modal/DirectMessageModal";
import ForwardMessageModal from "../modal/ForwardMessageModal";

import { useConversationsApi } from "../api/conversationsApi";

import { useContacts } from "../hooks/useContacts";
import { useUsers } from "../hooks/useUsers";
import { useTags } from "../hooks/useTags";
import { useFunnels } from "../hooks/useFunnels";
import { useConversations } from "../hooks/useConversations";
import { useAudioRecorder } from "../hooks/useAudioRecorder";
import { useQuickMessages } from "../hooks/useQuickMessages";
import { useAttachments } from "../hooks/useAttachments";
import { usePins } from "../hooks/usePins";
import { useDebounce } from "../hooks/useDebounce";
import { useConversationSearch } from "../hooks/useConversationSearch";
import { useConversationEvents } from "../hooks/useConversationEvents";
import { useMessageComposer } from "../hooks/useMessageComposer";

import { tomorrowCursor } from "../utils/helpers";

// `search` não entra: o texto livre vai para o endpoint de busca, que usa
// índice. Deixá-lo aqui manteria a consulta com ILIKE varrendo mensagens.
const hasActiveFilters = (filters) =>
  filters.name.trim() !== "" ||
  filters.phone.trim() !== "" ||
  filters.status !== "" ||
  filters.unread ||
  filters.users.length > 0 ||
  filters.tags.length > 0 ||
  filters.steps.length > 0;

// Tela de conversas: junta a lista (ConversationsSidebar), o chat aberto
// (cabeçalho, mensagens e MessageComposer) e o painel do cliente. Os eventos em
// tempo real ficam em useConversationEvents; a caixa de mensagem, em
// useMessageComposer.
const Conversations = ({ focusConversationId = null, onFocusHandled }) => {
  const { sendAction, messages } = useWebSocket();
  const { user } = useAuth();

  const api = useConversationsApi();
  const { contacts } = useContacts();
  const { users } = useUsers();
  const { tags } = useTags();
  const { funnels } = useFunnels();
  const { quickMessages } = useQuickMessages();
  const { attachments, addAttachment } = useAttachments();
  const {
    conversations,
    setConversations,
    summarizeConversation,
    deleteConversation,
    loading,
    error,
    fetchConversations,
    fetchFilteredConversations,
    fetchMoreConversations,
  } = useConversations();
  const { pinnedIds, togglePinConversation } = usePins(user.id);
  const recorder = useAudioRecorder();

  const [selectedChat, setSelectedChat] = useState(null);
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [openDirectMessageModal, setOpenDirectMessageModal] = useState(false);
  const [openForwardMessageModal, setOpenForwardMessageModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [forwardMessage, setForwardMessage] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    name: "",
    phone: "",
    status: "",
    unread: false,
    users: [],
    tags: [],
    steps: [],
  });

  const composer = useMessageComposer({
    selectedChat,
    user,
    sendAction,
    addAttachment,
    audioBlob: recorder.audioBlob,
    cancelRecording: recorder.cancelRecording,
  });

  const closeDirectMessageModal = useCallback(() => setOpenDirectMessageModal(false), []);

  useConversationEvents({
    messages,
    conversations,
    setConversations,
    selectedChat,
    setSelectedChat,
    fetchConversations,
    userId: user?.id,
    onConversationStartedByMe: closeDirectMessageModal,
  });

  const debouncedFilters = useDebounce(filters, 500);
  const debouncedSearch = useDebounce(filters.search, 400);
  const searching = debouncedSearch.trim().length >= 2;
  const { result: searchResult, loading: searchLoading } = useConversationSearch(
    debouncedSearch,
    { enabled: searching },
  );

  // Abre uma conversa que pode não estar na lista carregada (busca no
  // histórico inteiro, ou pedida por outra tela): busca por id quando falta.
  const loadAndSelect = useCallback(
    async (conversationId) => {
      const conversation = await api.getById(conversationId);
      if (!conversation) return;

      setConversations((prev) =>
        prev.some((item) => item.id === conversation.id) ? prev : [conversation, ...prev],
      );
      setSelectedChat(conversation);
    },
    [api, setConversations],
  );

  const openFromSearch = useCallback(
    async (conversationId) => {
      const loaded = conversations.find((item) => item.id === conversationId);
      if (loaded) {
        setSelectedChat(loaded);
        return;
      }

      try {
        await loadAndSelect(conversationId);
      } catch (err) {
        console.error("Erro ao abrir resultado da busca:", err);
        toast.error("Não foi possível abrir a conversa.");
      }
    },
    [conversations, loadAndSelect],
  );

  const updateFilters = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Abre a conversa pedida por outra tela. Se ela não estiver na lista carregada
  // (fora do período), busca sozinha em vez de deixar o gestor procurar.
  useEffect(() => {
    if (!focusConversationId || loading) return;

    const alreadyLoaded = conversations.find(
      (conversation) => conversation.id === focusConversationId,
    );

    if (alreadyLoaded) {
      setSelectedChat(alreadyLoaded);
      onFocusHandled?.();
      return;
    }

    let cancelled = false;

    api
      .getById(focusConversationId)
      .then((conversation) => {
        if (cancelled || !conversation) return;
        setConversations((prev) =>
          prev.some((item) => item.id === conversation.id) ? prev : [conversation, ...prev],
        );
        setSelectedChat(conversation);
      })
      .catch((err) => {
        console.error("Erro ao abrir conversa:", err);
        if (!cancelled) toast.error("Não foi possível abrir a conversa.");
      })
      .finally(() => {
        if (!cancelled) onFocusHandled?.();
      });

    return () => {
      cancelled = true;
    };
  }, [api, conversations, focusConversationId, loading, onFocusHandled, setConversations]);

  useEffect(() => {
    if (hasActiveFilters(debouncedFilters)) {
      // Sem o termo: com outros filtros ativos, mandá-lo junto reintroduziria o
      // ILIKE sobre mensagens que este trabalho justamente tirou do caminho.
      fetchFilteredConversations({ ...debouncedFilters, search: "" });
    } else {
      fetchConversations(tomorrowCursor());
    }
  }, [debouncedFilters, fetchConversations, fetchFilteredConversations]);

  const handleOpenForwardModal = useCallback((message) => {
    setForwardMessage(message);
    setOpenForwardMessageModal(true);
  }, []);

  const handleCloseForwardModal = useCallback(() => {
    setForwardMessage(null);
    setOpenForwardMessageModal(false);
  }, []);

  const handleForwardMessage = useCallback(
    (contacts) => {
      if (!forwardMessage) return;

      sendAction("forward_message", {
        messageId: forwardMessage,
        contacts,
      });

      handleCloseForwardModal();
    },
    [forwardMessage, sendAction, handleCloseForwardModal],
  );

  const markAsRead = async () => {
    try {
      const id = selectedChat.id;

      const conversation = await api.markAsRead(id);

      setConversations((prev) => prev.map((c) => (c.id === id ? conversation : c)));

      setSelectedChat((prev) => (prev && prev.id === id ? conversation : prev));
    } catch (error) {
      toast.error(error);
      console.error("Erro ao atualizar status:", error);
    }
  };

  const { unreadConversationsCount, archivedUnreadCount } = useMemo(
    () =>
      conversations.reduce(
        (acc, conv) => {
          if (conv._count.messages > 0) {
            acc.unreadConversationsCount += 1;
            if (conv.status === "ARQUIVADO") acc.archivedUnreadCount += 1;
          }
          return acc;
        },
        { unreadConversationsCount: 0, archivedUnreadCount: 0 },
      ),
    [conversations],
  );

  useEffect(() => {
    document.title =
      unreadConversationsCount > 0 ? `ChatJuri (${unreadConversationsCount})` : "ChatJuri";
  }, [unreadConversationsCount]);

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <p className="mb-4 text-red-500">Erro: {error}</p>
          <button
            // Sem a arrow, o onClick entrega o evento de clique como cursor de
            // data e a nova tentativa falha com "Invalid Date" no Prisma.
            onClick={() => fetchConversations(tomorrowCursor())}
            className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <ConversationsSidebar
        filters={filters}
        updateFilters={updateFilters}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters((prev) => !prev)}
        onNewConversation={() => setOpenDirectMessageModal(true)}
        archivedUnreadCount={archivedUnreadCount}
        users={users}
        tags={tags}
        funnels={funnels}
        searching={searching}
        searchResult={searchResult}
        searchLoading={searchLoading}
        onOpenSearchResult={openFromSearch}
        loading={loading}
        conversations={conversations}
        pinnedIds={pinnedIds}
        selectedChat={selectedChat}
        setSelectedChat={setSelectedChat}
        onLoadMore={fetchMoreConversations}
      />

      {!selectedChat ? (
        <div className="flex flex-1 items-center justify-center bg-gray-100 dark:bg-gray-900">
          <div className="text-center">
            <MessageSquare className="mx-auto mb-4 h-16 w-16 text-gray-300 dark:text-gray-600" />
            <p className="text-lg text-gray-500 dark:text-gray-400">
              Selecione uma conversa para começar
            </p>
          </div>
        </div>
      ) : (
        <div className="flex flex-1 flex-col bg-white dark:bg-gray-800">
          <ChatHeader
            selectedChat={selectedChat}
            pinnedIds={pinnedIds}
            togglePinConversation={togglePinConversation}
            markAsRead={markAsRead}
            setShowContactInfo={setShowContactInfo}
          />

          <ChatMessages
            messages={selectedChat.messages}
            contact={selectedChat.contact}
            conversationId={selectedChat.id}
            sendAction={sendAction}
            onReplyMessage={composer.selectQuoteMessage}
            onForwardMessage={handleOpenForwardModal}
          />

          <MessageComposer
            selectedChat={selectedChat}
            composer={composer}
            recorder={recorder}
            quickMessages={quickMessages}
            attachments={attachments}
          />
        </div>
      )}

      {showContactInfo && (
        <ClientSidebar
          api={api}
          user={user}
          toast={toast}
          selectedChat={selectedChat}
          setConversations={setConversations}
          setSelectedChat={setSelectedChat}
          summarizeConversation={summarizeConversation}
          deleteConversation={deleteConversation}
          users={users}
          funnels={funnels}
          tags={tags}
        />
      )}

      {openDirectMessageModal && (
        <DirectMessageList
          onClose={closeDirectMessageModal}
          contacts={contacts}
          user={user}
          sendAction={sendAction}
          setConversations={setConversations}
          setSelectedChat={setSelectedChat}
          toast={toast}
          api={api}
        />
      )}

      {openForwardMessageModal && (
        <ForwardMessageModal
          onClose={handleCloseForwardModal}
          contacts={contacts}
          onForward={handleForwardMessage}
        />
      )}
    </>
  );
};

export default Conversations;
